import { useEffect, useCallback, type KeyboardEvent } from 'react';
import { EVENT_KEY } from '../types/enums';

/**
 * @description This hook listens for clicks outside a specified HTML element, triggering a callback when detected.
 * @param ref specific HTML element reference
 * @param callback function containing action to be perform if outside clicked found
 * @param deps array of dependency to re-run method
 * @param root base HTML element reference for event listener attachment
 */
const useClickOutsideDetector = (
  ref?: React.RefObject<HTMLElement>,
  callback?: () => void,
  deps?: ReadonlyArray<unknown>,
  root?: React.RefObject<HTMLElement>,
): void => {
  const onClickOutside = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      if (!ref?.current?.contains(event.target as HTMLElement)) {
        callback?.();
      }
    },
    [ref, callback],
  );
  const keyDownEvent = (e: KeyboardEvent) => {
    if (e.key === EVENT_KEY.ENTER) {
      onClickOutside(e);
    }
  };
  useEffect(() => {
    if (!ref?.current) {
      return undefined;
    }
    const rootNode = root?.current ?? document;
    rootNode.addEventListener('click', onClickOutside as EventListener);
    rootNode.addEventListener('keydown', keyDownEvent as unknown as EventListener);

    return () => {
      rootNode.removeEventListener('click', onClickOutside as EventListener);
      rootNode.removeEventListener('keydown', keyDownEvent as unknown as EventListener);
    };
  }, [ref?.current, ...(deps ?? [])]);
};

export default useClickOutsideDetector;
